import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PackageSearch, Trash2, AlertTriangle, Eye, Gavel, Search } from "lucide-react";

const STATUS_LABELS = { active: "Actief", draft: "Concept", sold: "Verkocht", paused: "Gepauzeerd" };

export default function AdminProducts() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [deleting, setDeleting] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    api.get("/admin/products").then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    else if (user === false || (user && user.role !== "admin")) setLoading(false);
  }, [user]);

  if (user === false) return <Navigate to="/auth" replace />;
  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const del = async (p) => {
    if (!window.confirm(`Advertentie "${p.title || "(zonder titel)"}" definitief verwijderen? Bijbehorende biedingen en gesprekken worden ook verwijderd.`)) return;
    setDeleting(p.id);
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Advertentie verwijderd");
      setProducts((list) => list.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setDeleting(null);
    }
  };

  const bulkDelete = async () => {
    const ids = filtered.map((p) => p.id);
    if (!ids.length) return;
    if (!window.confirm(`Weet je zeker dat je ${ids.length} advertentie${ids.length > 1 ? "s" : ""} definitief wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk-delete", { product_ids: ids });
      toast.success(`${data.deleted} advertentie${data.deleted === 1 ? "" : "s"} verwijderd`);
      const idset = new Set(ids);
      setProducts((list) => list.filter((x) => !idset.has(x.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = products.filter((p) => {
    if (filter === "incomplete" && !p.incomplete) return false;
    if (filter !== "all" && filter !== "incomplete" && p.status !== filter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (p.title || "").toLowerCase().includes(q) || (p.seller_email || "").toLowerCase().includes(q);
  });

  const incompleteCount = products.filter((p) => p.incomplete).length;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="admin-products-page">
      <div className="flex items-center gap-2 mb-1">
        <PackageSearch className="h-5 w-5 text-rose-400" />
        <h1 className="font-serif text-3xl font-semibold text-zinc-100">Advertenties beheren</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">Verwijder lege of onjuiste advertenties. Als beheerder kun je elke advertentie verwijderen.</p>

      {!loading && products.length > 0 && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-rose-900/50 bg-rose-950/20 px-4 py-3">
          <span className="text-sm text-rose-100">
            {filtered.length} advertentie{filtered.length === 1 ? "" : "s"} in de huidige weergave.
          </span>
          <button
            data-testid="bulk-delete-button"
            onClick={bulkDelete}
            disabled={bulkBusy || filtered.length === 0}
            className="shrink-0 flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> {bulkBusy ? "Verwijderen..." : `Verwijder alle (${filtered.length})`}
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input
            data-testid="admin-products-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op titel of verkoper..."
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900/60 pl-10 pr-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { k: "all", label: `Alle (${products.length})` },
            { k: "incomplete", label: `Onvolledig (${incompleteCount})` },
            { k: "active", label: "Actief" },
            { k: "draft", label: "Concept" },
            { k: "sold", label: "Verkocht" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${filter === f.k ? "border-rose-600 bg-rose-950/40 text-rose-200" : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-24 text-zinc-500">Laden...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 rounded-xl border border-zinc-800 bg-zinc-900/40">
          <PackageSearch className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
          <p className="font-serif text-xl text-zinc-300">Geen advertenties gevonden</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-${p.id}`}
              className={`flex items-center gap-4 rounded-xl border p-3 ${p.incomplete ? "border-amber-800/50 bg-amber-950/10" : "border-zinc-800 bg-zinc-900/40"}`}
            >
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-zinc-950">
                {p.images && p.images.length ? (
                  <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-[10px] text-zinc-600">Geen foto</div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link to={`/product/${p.id}`} className="text-sm font-medium text-zinc-100 truncate hover:text-rose-300">
                    {p.title || <span className="italic text-zinc-500">(zonder titel)</span>}
                  </Link>
                  {p.incomplete && (
                    <span className="flex items-center gap-1 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-medium px-2 py-0.5" title="Onvolledig: minder dan 2 foto's of ontbrekende titel/beschrijving">
                      <AlertTriangle className="h-3 w-3" /> Onvolledig
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate">
                  € {Number(p.price || 0).toFixed(2)} · {STATUS_LABELS[p.status] || p.status} · {p.seller_email}
                </div>
                <div className="text-[11px] text-zinc-600 flex items-center gap-3 mt-0.5">
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {p.views || 0}</span>
                  {p.bid_count > 0 && <span className="flex items-center gap-1 text-rose-300"><Gavel className="h-3 w-3" /> {p.bid_count}</span>}
                  <span>{(p.images || []).length} foto's</span>
                </div>
              </div>
              <button
                data-testid={`delete-product-${p.id}`}
                onClick={() => del(p)}
                disabled={deleting === p.id}
                className="flex items-center gap-1.5 shrink-0 rounded-lg border border-rose-900/60 bg-rose-950/20 px-3 py-2 text-xs font-medium text-rose-300 hover:bg-rose-950/50 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" /> {deleting === p.id ? "..." : "Verwijder"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
